import { useEffect, useState } from 'react';
import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query';
import {
  GroupEntityTagsContext,
  GroupEntityTagsDispatchContext,
  GroupEntityTagsFetchContext,
  GroupEntityTagsLoadingContext,
  GroupEntityTagsSavingContext,
  GroupEntityTagsUpdateContext,
} from '@trinity/chat-components/contexts/group-tags/index.js';
import type { GroupTagsContextValue } from '@trinity/chat-components/contexts/group-tags/index.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { GroupTagUpdateItem } from '@trinity/chat';

type Props = {
  projectParentId: number;
} & ChildrenProps;

export default function GroupProjectParentTagsContainer({ children, projectParentId }: Props) {
  const [state, dispatch] = useState<GroupTagsContextValue>(GroupProjectParentTagsContainer.initialState);

  const apiClient = useChatApiClient();

  const query = useQuery({
    queryKey: ['get:group/tags/ProjectParent', projectParentId],
    queryFn: () => {
      return apiClient.tags.getProjectParentTags({ projectParentId });
    },
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (query.isSuccess && query.data?.tags) {
      dispatch(query.data.tags);
    }
  }, [query.data?.tags, query.isSuccess]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (tags: GroupTagUpdateItem[]) => {
      return apiClient.tags.updateProjectParentTags({
        projectParentId,
        tags,
      });
    },
    onSuccess: result => {
      query.refetch();
    },
  });

  return (
    <GroupEntityTagsContext.Provider value={state}>
      <GroupEntityTagsLoadingContext.Provider value={query.isLoading}>
        <GroupEntityTagsFetchContext.Provider value={query.refetch}>
          <GroupEntityTagsUpdateContext.Provider value={mutateAsync}>
            <GroupEntityTagsDispatchContext.Provider value={dispatch}>
              <GroupEntityTagsSavingContext.Provider value={isPending}>
                {children}
              </GroupEntityTagsSavingContext.Provider>
            </GroupEntityTagsDispatchContext.Provider>
          </GroupEntityTagsUpdateContext.Provider>
        </GroupEntityTagsFetchContext.Provider>
      </GroupEntityTagsLoadingContext.Provider>
    </GroupEntityTagsContext.Provider>
  );
}

const initial: GroupTagsContextValue = [];

GroupProjectParentTagsContainer.initialState = initial;

export { GroupProjectParentTagsContainer };